import React, { useEffect, useState, useMemo } from 'react';
import { Formik } from 'formik';
import { PropTypes } from 'prop-types';
import { useNavigate } from 'react-router-dom';
import * as Yup from 'yup';
import { Col, Row } from 'antd';
import { Form, Input, Select } from 'formik-antd';
import { useDispatch } from 'react-redux';
import { Box, FormInput, Button, CustomInput } from 'atoms';
import { addUser } from 'store/pageSlice';
import { getClientList } from 'store/clientSlice';
import { Debug } from './Debug';
import { ACL } from 'components/common';
import { ResetBtn } from 'components/reset-password';
import { CancelButton } from 'utils/commonStyled';

export default function DriverForm({ isEditMode, formData = {}, vendorData }) {
	const dispatch = useDispatch();
	const navigate = useNavigate();
	const [initialValues, setInitialValues] = useState({});
	const [vehicleTypeData, setVehicleTypedata] = useState('');

	const FormSchema = useMemo(
		() =>
			Yup.object({
				vendor_id: Yup.string().label('Vendor name').required().trim(''),
				vehicle_type: Yup.string().label('Vehicle Type').required().trim(''),
				password: Yup.string()
					.label('Password')
					.min(6)
					.max(12)
					.when([], {
						is: () => !isEditMode,
						then: Yup.string().required(),
						otherwise: Yup.string().notRequired(),
					}),
			}),
		[isEditMode]
	);

	useEffect(() => {
		setInitialValues({
			vendor_id: formData?.vendor_id || undefined,
			password: undefined,
			vendor: formData?.info?.vendor || undefined,
			vehicle_type: formData?.vehicle_type || undefined,
			username: formData?.username || undefined,
			driver_name: formData?.driver_name || undefined,
			city: formData?.city || undefined,
			mobile_no: formData?.mobile_no || undefined,
		});
	}, [formData]);

	function handleVendorsChange(e, setFieldValue) {
		if (setFieldValue) {
			setFieldValue('vehicle_type', undefined);
		}
		dispatch(
			getClientList({
				search_type: 'vehicle',
				filter_by: {
					vendor_id: e,
				},
			})
		).then((res) => {
			const data = res?.data?.vehicle_types;
			if (data.length !== 0) {
				const val = [];
				data.forEach((element) => {
					val.push({
						value: element,
						label: element,
					});
				});
				setVehicleTypedata(val);
			} else {
				setVehicleTypedata([]);
			}
		});
	}

	const handleSubmit = (event, values, { setErrors, setSubmitting }) => {
		event.preventDefault();

		const payload = {
			data: {
				password: values?.password,
				vehicle_type: values?.vehicle_type,
				vendor_id: values.vendor_id,
			},
		};

		if (isEditMode) {
			payload.update_type = 'driver';
		} else {
			payload.create_type = 'driver';
		}

		console.log(payload);

		dispatch(addUser({ data: payload, setErrors, isEditMode }))
			.then(() => {
				setSubmitting(false);
				navigate('/drivers');
			})
			.catch(() => {
				setSubmitting(false);
			});
	};

	return (
		<Formik
			initialValues={initialValues}
			validationSchema={FormSchema}
			onSubmit={handleSubmit}
			enableReinitialize
		>
			{({ values, errors, setFieldValue, isSubmitting }) => (
				<Form auto>
					<ACL action="update" module="driver">
						{isEditMode && (
							<Row gutter={16} justify="space-between">
								<ResetBtn username={formData?.username} />
							</Row>
						)}
					</ACL>
					{console.log('values', values)}
					{console.log('errors', errors)}
					<Box padding="1.5rem 2rem" background="#fff">
						<Row gutter={16} justify="space-between">
							<Col lg={12}>
								<FormInput name="vendor_id" label="Select Vendor *">
									<Select
										autoComplete="new-password"
										showSearch
										onClear
										name="vendor_id"
										disabled={isEditMode}
										filterOption={(input, option) =>
											option.label
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
										options={vendorData}
										placeholder="Select"
										onChange={(e) => handleVendorsChange(e, setFieldValue)}
									/>
								</FormInput>
							</Col>
							<Col lg={12}>
								<FormInput name="vehicle_type" label="Vehicle Type *">
									<Select
										showSearch
										onClear
										name="vehicle_type"
										disabled={isEditMode}
										filterOption={(input, option) =>
											option.label
												.toLowerCase()
												.indexOf(input.toLowerCase()) >= 0
										}
										placeholder="Select"
										options={vehicleTypeData}
									/>
								</FormInput>
							</Col>
						</Row>
						{isEditMode && (
							<>
								<Row gutter={16} justify="space-between">
									<Col lg={12}>
										<FormInput name="username" label="Username *">
											<CustomInput name="username" disabled={isEditMode} />
										</FormInput>
									</Col>
									<Col lg={12}>
										<FormInput name="driver_name" label="Driver name *">
											<CustomInput name="driver_name" disabled={isEditMode} />
										</FormInput>
									</Col>
								</Row>
								<Row gutter={16} justify="space-between">
									<Col lg={12}>
										<FormInput name="city" label="City *">
											<CustomInput name="city" disabled={isEditMode} />
										</FormInput>
									</Col>
									<Col lg={12}>
										<FormInput name="mobile_no" label="Mobile number *">
											<CustomInput name="mobile_no" disabled={isEditMode} />
										</FormInput>
									</Col>
								</Row>
							</>
						)}
						<Row gutter={16} justify="space-between">
							<Col lg={12}>
								{!isEditMode && (
									<FormInput name="password" label="Password *">
										<Input.Password
											name="password"
											autoComplete="new-password"
											role="presentation"
										/>
									</FormInput>
								)}
							</Col>
						</Row>
					</Box>
					{!isEditMode && (
						<Box backgroundColor="#f5f5f5" textAlign="right" padding="1.5rem">
							<CancelButton onClick={() => navigate('/drivers')} type="button">
								Cancel
							</CancelButton>
							<Button
								borderRadius="4px"
								backgroundColor="#18405d"
								disabled={isSubmitting || Object.keys(errors).length > 0}
								type="submit"
								onClick={(e) =>
									handleSubmit(e, values, {
										setErrors: () => {},
										setSubmitting: () => {},
									})
								}
							>
								{isEditMode ? 'Update' : 'Submit'}
							</Button>
						</Box>
					)}
					<Debug />
				</Form>
			)}
		</Formik>
	);
}

DriverForm.propTypes = {
	isEditMode: PropTypes.bool,
	formData: PropTypes.object,
	vendorData: PropTypes.array,
};
